import type { ReactNode } from "react"
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  Bell,
  BookOpen,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  ChartColumn,
  Check,
  CheckCheck,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleMinus,
  CircleX,
  ClipboardCheck,
  ClockAlert,
  Database,
  Download,
  ExternalLink,
  FileBadge,
  FilePlus,
  FileSearch,
  FileStack,
  FileText,
  FileX,
  HardDrive,
  Hash,
  History,
  Hourglass as Ampulheta, // anti-ia-ok: nome do ícone em lucide-react, não vidro/brilho
  Info,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  LogIn,
  LogOut,
  Menu as IconeDeMenu,
  MessageSquareWarning,
  Network,
  IdCard,
  Milestone,
  Minus,
  PanelLeftClose,
  PanelLeftOpen,
  PauseCircle,
  PencilLine,
  Plus,
  Radio,
  RefreshCw,
  RotateCcw,
  Save,
  Scale,
  Search,
  Send,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Signature,
  Server,
  Settings,
  Moon,
  Sun,
  Tag,
  TriangleAlert,
  User,
  Users,
  X,
  type LucideIcon,
  ChevronRight,
  ChevronLeftIcon,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Ellipsis,
  Filter,
  Copy,
  CloudDownload,
  Mail,
  Phone,
  ListClock,
  EyeOff,
  Lock,
} from "lucide-react"

export type PropsDoIcone = {
  titulo?: string
  tamanho?: number
}

export type Icone = (props: PropsDoIcone) => ReactNode

function envolver(IconeDeOrigem: LucideIcon): Icone {
  return function IconeEnvolvido({ titulo, tamanho = 14 }: PropsDoIcone) {
    const classe = `icone size-[${tamanho}px]`
    if (titulo === undefined) {
      return (
        <IconeDeOrigem className={classe} width={tamanho} height={tamanho} strokeWidth={1.75} />
      )
    }
    return (
      <IconeDeOrigem
        className={classe}
        width={tamanho}
        height={tamanho}
        strokeWidth={1.75}
        role="img"
      >
        <title>{titulo}</title>
      </IconeDeOrigem>
    )
  }
}

export const Atrasada: Icone = envolver(ClockAlert)
export const Pendente: Icone = envolver(Circle)
export const Concluida: Icone = envolver(CircleCheck)
export const Dispensada: Icone = envolver(CircleMinus)
export const Validada: Icone = envolver(Check)
export const Recusada: Icone = envolver(CircleX)
export const Ressalva: Icone = envolver(TriangleAlert)
export const Anotacao: Icone = envolver(PencilLine)
export const Cancelada: Icone = envolver(FileX)
export const Juntada: Icone = envolver(FilePlus)
export const Conferida: Icone = envolver(ShieldCheck)

export const Laudo: Icone = envolver(FileBadge)
export const Assinar: Icone = envolver(Signature)

export const Vencido: Icone = envolver(CalendarX)
export const Vencendo: Icone = envolver(CalendarClock)
export const Vigente: Icone = envolver(CalendarCheck)
export const Suspenso: Icone = envolver(PauseCircle)
export const Encerrado: Icone = envolver(Archive)

export const Reabrir: Icone = envolver(RotateCcw)
export const Concluir: Icone = Validada
export const Marcado: Icone = Validada
export const OpcaoEscolhida: Icone = Pendente
export const Dispensar: Icone = envolver(Minus)
export const Abrir: Icone = envolver(ExternalLink)
export const Baixar: Icone = envolver(Download)
export const Validar: Icone = Validada
export const ValidarTodos: Icone = envolver(CheckCheck)
export const Contestar: Icone = envolver(MessageSquareWarning)
export const Recalcular: Icone = envolver(RefreshCw)
export const Enviar: Icone = envolver(Send)
export const Salvar: Icone = envolver(Save)
export const Voltar: Icone = envolver(ArrowLeft)
export const Avancar: Icone = envolver(ArrowRight)
export const Mais: Icone = envolver(Plus)
export const Sino: Icone = envolver(Bell)
export const Sair: Icone = envolver(LogOut)
export const Entrar: Icone = envolver(LogIn)
export const Lua: Icone = envolver(Moon)
export const Sol: Icone = envolver(Sun)

export const Proxima: Icone = envolver(ChevronRight)
export const Anterior: Icone = envolver(ChevronLeftIcon)
export const Aumentar: Icone = envolver(ChevronDown)
export const Diminuir: Icone = envolver(ChevronUp)

export const Buscar: Icone = envolver(Search)
export const Fechar: Icone = envolver(X)

export const Pessoa: Icone = envolver(User)
export const Chave: Icone = envolver(KeyRound)
export const Numero: Icone = envolver(Hash)
export const Copiar: Icone = envolver(Copy)
export const Email: Icone = envolver(Mail)
export const Telefone: Icone = envolver(Phone)
export const Cadeado: Icone = envolver(Lock)
export const Oculto: Icone = envolver(EyeOff)

export const Etiqueta: Icone = envolver(Tag)
export const Organograma: Icone = envolver(Network)
export const Cargo: Icone = envolver(IdCard)
export const EmBreve: Icone = envolver(Ampulheta)
export const Fundamento: Icone = envolver(BookOpen)
export const Informacao: Icone = envolver(Info)

export const Orgao: Icone = envolver(Landmark)
export const Empresa: Icone = envolver(Building2)
export const Pessoas: Icone = envolver(Users)
export const Permissao: Icone = envolver(Shield)
export const Integracao: Icone = envolver(Link2)
export const Leitura: Icone = envolver(FileSearch)
export const Catalogo: Icone = envolver(LayoutGrid)
export const Canal: Icone = envolver(Radio)
export const LimiteLegal: Icone = envolver(Scale)
export const Providencia: Icone = envolver(Milestone)
export const TipoDePeca: Icone = envolver(FileStack)
export const Historico: Icone = envolver(History)
export const HistoricoDeExecucoes: Icone = envolver(ListClock)
export const ImportarDaNuvem: Icone = envolver(CloudDownload)
export const OcultarMenu: Icone = envolver(PanelLeftClose)
export const MostrarMenu: Icone = envolver(PanelLeftOpen)
export const Menu: Icone = envolver(IconeDeMenu)
export const Painel: Icone = envolver(LayoutDashboard)
export const Contrato: Icone = envolver(FileText)
export const Relatorio: Icone = envolver(ChartColumn)
export const Fiscalizacao: Icone = envolver(ClipboardCheck)
export const Configuracao: Icone = envolver(Settings)
export const Servidor: Icone = envolver(Server)
export const BancoDeDados: Icone = envolver(Database)
export const Armazenamento: Icone = envolver(HardDrive)
export const Risco: Icone = envolver(ShieldAlert)
export const Erro: Icone = envolver(CircleAlert)
export const OrdenarColunas: Icone = envolver(ArrowUpDown)
export const Filtrar: Icone = envolver(Filter)
export const MaisAcoes: Icone = envolver(Ellipsis)
